import { useContext, useEffect } from "react";
import { AuthContext } from "../../Moduels/Context/AuthContext";
import { useEmployees } from "../../hooks/useEmployees";
import {
  buildSelfEmployeeFromProfile,
  formatEmployeeLabel,
  isEmployeeSelfService,
  resolveEmployeeIdFromProfile,
} from "./employeeUtils";

const DEFAULT_SELECT =
  "border border-slate-300 p-2 w-full rounded-lg bg-white text-sm";

/**
 * Reusable employee picker — loads org employees once and shows name (code).
 * For employee logins, locks to the signed-in employee only (no org-wide list).
 */
export default function EmployeeSelect({
  value = "",
  onChange,
  name,
  required = false,
  allowEmpty = true,
  emptyLabel = "Select employee",
  label,
  className = "",
  selectClassName = DEFAULT_SELECT,
  id,
  disabled = false,
  employees: employeesProp,
  hideLabel = false,
  loadingHint = true,
  extraOptions = [],
}) {
  const { user, role, principalType } = useContext(AuthContext);
  const selfService = isEmployeeSelfService(user, role, principalType);
  const selfEmployee = buildSelfEmployeeFromProfile(user);
  const selfEmployeeId = resolveEmployeeIdFromProfile(user);

  const { employees: loaded, loading } = useEmployees({
    enabled: !selfService && !employeesProp,
  });
  const employees = employeesProp ?? loaded;

  useEffect(() => {
    if (!selfService || !selfEmployeeId) return;
    if (!value || String(value) !== String(selfEmployeeId)) {
      onChange?.(String(selfEmployeeId), selfEmployee);
    }
    // onChange is often an inline handler; sync only when auth/value changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selfService, selfEmployeeId, selfEmployee, value]);

  const handleSelect = (e) => {
    const nextId = e.target.value;
    const extra = extraOptions.find((x) => String(x.value) === String(nextId));
    if (extra) {
      onChange?.(nextId, extra);
      return;
    }
    const emp = employees.find((x) => String(x.employee_id) === String(nextId));
    onChange?.(nextId, emp || null);
  };

  if (selfService && selfEmployeeId) {
    const lockedField = (
      <div
        className={`${selectClassName} bg-slate-50 text-slate-700 cursor-default`}
        aria-readonly="true"
      >
        {formatEmployeeLabel(selfEmployee) || "Your profile"}
      </div>
    );

    if (hideLabel && !label) {
      return lockedField;
    }

    return (
      <div className={className}>
        {label && (
          <label htmlFor={id} className="block text-sm font-medium text-slate-700 mb-1">
            {label}
            {required ? " *" : ""}
          </label>
        )}
        {lockedField}
      </div>
    );
  }

  const field =
    loading && employees.length === 0 ? (
      <select
        id={id}
        name={name}
        className={selectClassName}
        disabled
        aria-busy="true"
      >
        <option value="">Loading employees…</option>
      </select>
    ) : employees.length > 0 ? (
      <select
        id={id}
        name={name}
        value={value}
        onChange={handleSelect}
        className={selectClassName}
        required={required}
        disabled={disabled || loading}
      >
        {allowEmpty && <option value="">{emptyLabel}</option>}
        {extraOptions.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
        {employees.map((emp) => (
          <option key={emp.employee_id} value={emp.employee_id}>
            {formatEmployeeLabel(emp)}
          </option>
        ))}
      </select>
    ) : (
      <select
        id={id}
        name={name}
        value=""
        className={selectClassName}
        disabled
      >
        <option value="">{emptyLabel} — none available</option>
      </select>
    );

  if (hideLabel && !label) {
    return (
      <>
        {field}
        {loadingHint && loading && employees.length === 0 && (
          <p className="text-xs text-slate-400 mt-1">Loading employees…</p>
        )}
      </>
    );
  }

  return (
    <div className={className}>
      {label && (
        <label htmlFor={id} className="block text-sm font-medium text-slate-700 mb-1">
          {label}
          {required ? " *" : ""}
        </label>
      )}
      {field}
      {loadingHint && loading && employees.length === 0 && (
        <p className="text-xs text-slate-400 mt-1">Loading employees…</p>
      )}
    </div>
  );
}
